import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { School, Scale, Home, Wrench, Heart, Briefcase, Wine, Crown, HelpCircle, Calendar, Upload, Check, ChevronLeft, ChevronRight, Video, Phone, MessageCircle, CheckCircle, AlertCircle, CreditCard, DollarSign, X, Loader2 } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { bookingService } from '../services/bookingService';
import { directBookingService } from '../services/directBookingService';
import { availabilityService, TimeSlot } from '../services/availabilityService';
import { ServiceType } from '../types/appointment';
import { createCheckoutFromBooking } from '../services/paymentService';
import { PayPalPaymentButtons } from './PayPalPaymentButtons';
import { APP_TIMEZONE } from '../utils/timezone';
import { BOOKING_CONFIGS, BookingType } from '../types/booking';

interface BookingPageProps {
  onNavigateToHome: () => void;
  onNavigateToServices: () => void;
  preSelectedService?: string;
  isPaidSession?: boolean;
}

export function BookingPage({ onNavigateToHome, onNavigateToServices, preSelectedService, isPaidSession = false }: BookingPageProps) {
  const { t } = useTranslation();
  
  const services = [
    { id: 'school', icon: School },
    { id: 'legal', icon: Scale },
    { id: 'realestate', icon: Home },
    { id: 'homemanagement', icon: Wrench },
    { id: 'spiritual', icon: Heart },
    { id: 'business', icon: Briefcase },
    { id: 'beverage', icon: Wine },
    { id: 'concierge', icon: Crown },
    { id: 'unsure', icon: HelpCircle },
  ];

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(preSelectedService || 'unsure');
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<Array<{ date: Date; time: string }>>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    meetingMethod: 'zoom' as 'zoom' | 'phone' | 'wechat',
    email: '',
    phone: '',
    phoneArea: '+1',
    wechatId: '',
    message: '',
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [addToCalendar, setAddToCalendar] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [userTimezone] = useState<string>(APP_TIMEZONE); // Fixed to America/Los_Angeles
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  const totalSteps = isPaidSession ? 3 : 2;
  const sessionPrice = 150;
  const sessionCount = selectedTimeSlots.length;
  const totalAmount = sessionCount * sessionPrice;

  // Load available time slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      setIsLoadingTimeSlots(true);
      setSelectedTime(''); // Reset selected time
      
      availabilityService
        .getAvailableTimeSlots(selectedDate)
        .then(slots => {
          setAvailableTimeSlots(slots);
        })
        .catch(error => {
          console.error('Error loading time slots:', error);
          // Fallback to empty array if error
          setAvailableTimeSlots([]);
        })
        .finally(() => {
          setIsLoadingTimeSlots(false);
        });
    } else {
      setAvailableTimeSlots([]);
    }
  }, [selectedDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      setSubmitError(t('booking.errorMessages.selectDateTime'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Prepare booking data
      const bookingType = isPaidSession ? 'paid-session' : 'free-consultation';
      const bookingData = {
        firstName: formData.firstName,
        email: formData.email, // Email is now always required
        phone: formData.meetingMethod === 'phone' ? formData.phone : undefined,
        wechatId: formData.meetingMethod === 'wechat' ? formData.wechatId : undefined,
        message: formData.message,
        selectedService: selectedService as ServiceType,
        selectedDate,
        selectedTime,
        timezone: userTimezone, // Include user's timezone
        meetingMethod: formData.meetingMethod, // Include the meeting method
        addToCalendar: addToCalendar, // Email confirmation preference
        smsReminder: false, // SMS reminders disabled
        uploadedFiles: uploadedFile ? [uploadedFile] : [],
        duration: BOOKING_CONFIGS[bookingType].duration, // Include correct duration
      };

      console.log('Submitting booking:', bookingData);

      // Try direct Firestore write first (for debugging)
      console.log('Attempting direct Firestore write...');
      const directResult = await directBookingService.createAppointmentDirectly(bookingData);

      if (directResult.success) {
        console.log('Direct Firestore write successful:', directResult.appointmentId);
        setIsSubmitted(true);
        return;
      } else {
        console.warn('Direct Firestore write failed, trying Cloud Function...', directResult.error);
        
        // Fallback to Cloud Function
        const result = await bookingService.createBooking(bookingData);

        if (result.success) {
          console.log('Cloud Function booking created successfully:', result.appointmentId);
          setIsSubmitted(true);
        } else {
          console.error('Both methods failed. Direct error:', directResult.error, 'Function error:', result.error);
          setSubmitError(`Both methods failed. Direct: ${directResult.error}, Function: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setSubmitError(t('booking.errorMessages.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndGoHome = () => {
    setStep(1);
    setSelectedService(preSelectedService || 'unsure');
    setFormData({ firstName: '', meetingMethod: 'zoom', email: '', phone: '', phoneArea: '+1', wechatId: '', message: '' });
    setSelectedDate(null);
    setSelectedTime('');
    setSelectedTimeSlots([]);
    setSelectedPaymentMethod(null);
    setUploadedFile(null);
    setIsSubmitted(false);
    onNavigateToHome();
  };

  const resetAndGoToServices = () => {
    setStep(1);
    setSelectedService(preSelectedService || 'unsure');
    setFormData({ firstName: '', meetingMethod: 'zoom', email: '', phone: '', phoneArea: '+1', wechatId: '', message: '' });
    setSelectedDate(null);
    setSelectedTime('');
    setSelectedTimeSlots([]);
    setSelectedPaymentMethod(null);
    setUploadedFile(null);
    setIsSubmitted(false);
    onNavigateToServices();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file.name);
    }
  };

  // Generate calendar days for current month view
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add actual days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      
      // Check if date is available for booking (not in the past, within 30 days)
      const isAvailable = availabilityService.isDateAvailable(date);
      
      if (isAvailable) {
        days.push(date);
      } else if (date < today) {
        days.push(null); // Past dates shown as disabled
      } else {
        days.push(date); // Future dates beyond 30 days still shown but will be disabled
      }
    }
    
    return days;
  };

  const nextMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + 1);
    
    // Don't go more than 2 months ahead (to keep within 30-day booking window)
    const today = new Date();
    const maxMonth = new Date(today.getFullYear(), today.getMonth() + 2, 1);
    
    if (next <= maxMonth) {
      setCurrentMonth(next);
    }
  };

  const prevMonth = () => {
    const prev = new Date(currentMonth);
    prev.setMonth(prev.getMonth() - 1);
    
    // Allow going back to current month only (not past months)
    const today = new Date();
    const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    if (prev >= minMonth) {
      setCurrentMonth(prev);
    }
  };

  const getServiceLabel = () => {
    return t(`booking.services.${selectedService}`, 'General Consultation');
  };

  // Create checkout when entering Step 3 (payment step)
  useEffect(() => {
    console.log('🔍 Step 3 effect triggered', {
      step,
      isPaidSession,
      hasCheckoutId: !!checkoutId,
      isCreatingCheckout,
      selectedService,
      sessionPrice,
      email: formData.email,
      firstName: formData.firstName,
      selectedTimeSlotsCount: selectedTimeSlots.length
    });

    if (step === 3 && isPaidSession && !checkoutId && !isCreatingCheckout) {
      console.log('🚀 Initiating checkout creation for payment step', {
        selectedService,
        sessionPrice,
        totalAmount,
        sessionCount,
        email: formData.email,
        firstName: formData.firstName
      });

      setIsCreatingCheckout(true);
      setSubmitError(null);
      
      createCheckoutFromBooking(
        selectedService,
        BOOKING_CONFIGS[isPaidSession ? 'paid-session' : 'free-consultation'].duration,
        sessionPrice,
        formData.email,
        formData.firstName
      )
        .then((checkout) => {
          console.log('✅ Checkout created successfully in BookingPage', {
            checkoutId: checkout.checkoutId,
            status: checkout.status,
            amount: checkout.amount,
            currency: checkout.currency
          });
          setCheckoutId(checkout.checkoutId);
        })
        .catch((error) => {
          console.error('❌ Error creating checkout in BookingPage', {
            error: error.message,
            stack: error.stack,
            selectedService,
            sessionPrice,
            email: formData.email
          });
          setSubmitError('Failed to initialize payment. Please try again.');
          setStep(2); // Go back to previous step
        })
        .finally(() => {
          console.log('🏁 Checkout creation completed', {
            success: !!checkoutId
          });
          setIsCreatingCheckout(false);
        });
    }
  }, [step, isPaidSession, checkoutId, isCreatingCheckout, selectedService, sessionPrice, formData.email, formData.firstName, totalAmount, sessionCount, selectedTimeSlots.length]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Handle payment success
  const handlePaymentSuccess = async (completedCheckoutId: string) => {
    console.log('🎉 handlePaymentSuccess called', {
      completedCheckoutId,
      selectedTimeSlots: selectedTimeSlots.length,
      timestamp: new Date().toISOString()
    });
    console.log('✅ Payment confirmed! Creating booking...', completedCheckoutId);
    setIsSubmitting(true);

    try {
      // Create booking with first time slot (for now)
      const firstSlot = selectedTimeSlots[0];
      
      console.log('📝 Preparing booking data', {
        firstName: formData.firstName,
        email: formData.email,
        service: selectedService,
        date: firstSlot.date.toISOString(),
        time: firstSlot.time,
        meetingMethod: formData.meetingMethod,
        timezone: userTimezone
      });

      const bookingData = {
        firstName: formData.firstName,
        email: formData.email,
        phone: formData.meetingMethod === 'phone' ? formData.phone : undefined,
        wechatId: formData.meetingMethod === 'wechat' ? formData.wechatId : undefined,
        message: formData.message,
        selectedService: selectedService as ServiceType,
        selectedDate: firstSlot.date,
        selectedTime: firstSlot.time,
        timezone: userTimezone,
        meetingMethod: formData.meetingMethod,
        addToCalendar: addToCalendar,
        smsReminder: false,
        uploadedFiles: uploadedFile ? [uploadedFile] : [],
        duration: BOOKING_CONFIGS['paid-session'].duration, // 30 minutes for paid sessions
        checkoutId: completedCheckoutId, // Link appointment to successful payment
      };

      console.log('💾 Creating appointment via directBookingService', { bookingData });
      const directResult = await directBookingService.createAppointmentDirectly(bookingData);

      if (directResult.success) {
        console.log('✅ Booking created successfully after payment', {
          appointmentId: directResult.appointmentId,
          checkoutId: completedCheckoutId
        });
        setIsSubmitted(true);
      } else {
        console.error('❌ Failed to create booking after payment', {
          checkoutId: completedCheckoutId,
          error: directResult.error
        });
        setSubmitError('Payment successful but booking creation failed. Please contact support.');
      }
    } catch (error: any) {
      console.error('❌ Exception creating booking after payment', {
        checkoutId: completedCheckoutId,
        error: error.message,
        stack: error.stack
      });
      setSubmitError('Payment successful but booking creation failed. Please contact support.');
    } finally {
      console.log('🏁 handlePaymentSuccess completed');
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (error: Error) => {
    console.error('❌ handlePaymentError called', {
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
    setSubmitError(error.message || 'Payment failed. Please try again.');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>
            {/* Header */}
            <div className="text-center mb-12">
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[#0A1A33] mb-3 text-[32px] font-bold"
              >
                {isPaidSession ? t('booking.header.paidSessionTitle') : t('booking.header.title')}
              </motion.h1>
              <p className="text-[#0A1A33]/70">
                {isPaidSession ? t('booking.header.paidSessionSubtitle') : t('booking.header.subtitle')}
              </p>

              {/* Progress Indicator */}
              <div className="flex items-center justify-center gap-2 mt-8">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
                  <div key={s} className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                        step >= s
                          ? 'bg-[#C6A667] text-white'
                          : 'bg-white text-[#0A1A33]/40'
                      }`}
                    >
                      {s}
                    </div>
                    {s < totalSteps && (
                      <div
                        className={`w-16 h-0.5 mx-2 transition-all ${
                          step > s ? 'bg-[#C6A667]' : 'bg-[#0A1A33]/20'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Service Selection & Form */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8 max-w-3xl mx-auto"
                >
                  {/* Service Selection */}
                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-[#0A1A33] mb-2">{t('booking.form.serviceSelection.title')}</h2>
                    <p className="text-[#0A1A33]/60 mb-6">
                      {t('booking.form.serviceSelection.subtitle')}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {services.map((service) => {
                        const Icon = service.icon;
                        return (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => setSelectedService(service.id)}
                            className={`p-4 rounded-xl border-2 transition-all text-left group ${
                              selectedService === service.id
                                ? 'border-[#C6A667] bg-[#C6A667]/5'
                                : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white'
                            }`}
                          >
                            <Icon
                              className={`w-5 h-5 mb-2 transition-colors ${
                                selectedService === service.id
                                  ? 'text-[#C6A667]'
                                  : 'text-[#0A1A33]/40 group-hover:text-[#C6A667]'
                              }`}
                            />
                            <div
                              className={`text-sm font-medium transition-colors ${
                                selectedService === service.id
                                  ? 'text-[#0A1A33]'
                                  : 'text-[#0A1A33]/70'
                              }`}
                            >
                              {t(`booking.services.${service.id}`)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-[#0A1A33] mb-6">{t('booking.form.personalInfo.title')}</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0A1A33]/80 mb-2">
                          {t('booking.form.personalInfo.firstName.label')} <span className="text-[#C6A667]">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={(e) =>
                            setFormData({ ...formData, firstName: e.target.value })
                          }
                          className="w-full px-4 py-3 rounded-lg border border-[#0A1A33]/20 focus:border-[#C6A667] focus:outline-none focus:ring-2 focus:ring-[#C6A667]/20 bg-white text-[#0A1A33] transition-all"
                          placeholder={t('booking.form.personalInfo.firstName.placeholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1A33]/80 mb-2">
                          {t('booking.form.personalInfo.email.label')} <span className="text-[#C6A667]">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="w-full px-4 py-3 rounded-lg border border-[#0A1A33]/20 focus:border-[#C6A667] focus:outline-none focus:ring-2 focus:ring-[#C6A667]/20 bg-white text-[#0A1A33] transition-all"
                          placeholder={t('booking.form.personalInfo.email.placeholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1A33]/80 mb-3">
                          {t('booking.form.personalInfo.meetingMethod.label')} <span className="text-[#C6A667]">*</span>
                        </label>
                        
                        {/* Meeting Method Selector */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, meetingMethod: 'zoom' })}
                            className={`px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                              formData.meetingMethod === 'zoom'
                                ? 'border-[#C6A667] bg-[#C6A667]/5'
                                : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white'
                            }`}
                          >
                            <Video className={`w-5 h-5 ${
                              formData.meetingMethod === 'zoom' ? 'text-[#C6A667]' : 'text-[#0A1A33]/40'
                            }`} />
                            <span className={`text-sm font-medium ${
                              formData.meetingMethod === 'zoom' ? 'text-[#0A1A33]' : 'text-[#0A1A33]/70'
                            }`}>
                              {t('booking.form.personalInfo.meetingMethod.zoom')}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, meetingMethod: 'phone' })}
                            className={`px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                              formData.meetingMethod === 'phone'
                                ? 'border-[#C6A667] bg-[#C6A667]/5'
                                : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white'
                            }`}
                          >
                            <Phone className={`w-5 h-5 ${
                              formData.meetingMethod === 'phone' ? 'text-[#C6A667]' : 'text-[#0A1A33]/40'
                            }`} />
                            <span className={`text-sm font-medium ${
                              formData.meetingMethod === 'phone' ? 'text-[#0A1A33]' : 'text-[#0A1A33]/70'
                            }`}>
                              {t('booking.form.personalInfo.meetingMethod.phone')}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, meetingMethod: 'wechat' })}
                            className={`px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                              formData.meetingMethod === 'wechat'
                                ? 'border-[#C6A667] bg-[#C6A667]/5'
                                : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white'
                            }`}
                          >
                            <MessageCircle className={`w-5 h-5 ${
                              formData.meetingMethod === 'wechat' ? 'text-[#C6A667]' : 'text-[#0A1A33]/40'
                            }`} />
                            <span className={`text-sm font-medium ${
                              formData.meetingMethod === 'wechat' ? 'text-[#0A1A33]' : 'text-[#0A1A33]/70'
                            }`}>
                              {t('booking.form.personalInfo.meetingMethod.wechat')}
                            </span>
                          </button>
                        </div>

                        {/* Additional Contact Info Based on Meeting Method */}
                        {formData.meetingMethod === 'phone' && (
                          <div>
                            <label className="block text-sm font-medium text-[#0A1A33]/80 mb-2">
                              {t('booking.form.personalInfo.phone.label')} <span className="text-[#C6A667]">*</span>
                            </label>
                            <div className="flex gap-2">
                              <select
                                value={formData.phoneArea}
                                onChange={(e) =>
                                  setFormData({ ...formData, phoneArea: e.target.value })
                                }
                                className="px-3 py-3 rounded-lg border border-[#0A1A33]/20 focus:border-[#C6A667] focus:outline-none focus:ring-2 focus:ring-[#C6A667]/20 bg-white text-[#0A1A33] transition-all"
                              >
                                <option value="+1">{t('booking.phoneCountries.us')}</option>
                                <option value="+86">{t('booking.phoneCountries.cn')}</option>
                                <option value="+852">{t('booking.phoneCountries.hk')}</option>
                                <option value="+853">{t('booking.phoneCountries.mo')}</option>
                                <option value="+886">{t('booking.phoneCountries.tw')}</option>
                                <option value="+65">{t('booking.phoneCountries.sg')}</option>
                                <option value="+44">{t('booking.phoneCountries.uk')}</option>
                              </select>
                              <input
                                type="tel"
                                required
                                value={formData.phone}
                                onChange={(e) =>
                                  setFormData({ ...formData, phone: e.target.value })
                                }
                                className="flex-1 px-4 py-3 rounded-lg border border-[#0A1A33]/20 focus:border-[#C6A667] focus:outline-none focus:ring-2 focus:ring-[#C6A667]/20 bg-white text-[#0A1A33] transition-all"
                                placeholder={t('booking.form.personalInfo.phone.placeholder')}
                              />
                            </div>
                          </div>
                        )}

                        {formData.meetingMethod === 'wechat' && (
                          <div>
                            <label className="block text-sm font-medium text-[#0A1A33]/80 mb-2">
                              {t('booking.form.personalInfo.wechatId.label')} <span className="text-[#C6A667]">*</span>
                            </label>
                            <input
                              type="text"
                              required
                              value={formData.wechatId}
                              onChange={(e) =>
                                setFormData({ ...formData, wechatId: e.target.value })
                              }
                              className="w-full px-4 py-3 rounded-lg border border-[#0A1A33]/20 focus:border-[#C6A667] focus:outline-none focus:ring-2 focus:ring-[#C6A667]/20 bg-white text-[#0A1A33] transition-all"
                              placeholder={t('booking.form.personalInfo.wechatId.placeholder')}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1A33]/80 mb-2">
                          {t('booking.form.personalInfo.message.label')}
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) =>
                            setFormData({ ...formData, message: e.target.value })
                          }
                          rows={4}
                          className="w-full px-4 py-3 rounded-lg border border-[#0A1A33]/20 focus:border-[#C6A667] focus:outline-none focus:ring-2 focus:ring-[#C6A667]/20 bg-white text-[#0A1A33] transition-all resize-none"
                          placeholder={t('booking.form.personalInfo.message.placeholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1A33]/80 mb-2">
                          {t('booking.form.personalInfo.fileUpload.label')}
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="file-upload"
                            accept=".pdf,.doc,.docx,.txt"
                          />
                          <label
                            htmlFor="file-upload"
                            className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border-2 border-dashed border-[#0A1A33]/20 hover:border-[#C6A667]/50 bg-white cursor-pointer transition-all group"
                          >
                            <Upload className="w-5 h-5 text-[#0A1A33]/40 group-hover:text-[#C6A667]" />
                            <span className="text-sm text-[#0A1A33]/60 group-hover:text-[#C6A667]">
                              {uploadedFile || t('booking.form.personalInfo.fileUpload.placeholder')}
                            </span>
                          </label>
                        </div>
                      </div>

                      <p className="text-xs text-[#0A1A33]/50 italic">
                        {t('booking.form.personalInfo.privacyNote')}
                      </p>
                    </div>
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={
                      !formData.firstName || 
                      !formData.email ||
                      (formData.meetingMethod === 'phone' && !formData.phone) ||
                      (formData.meetingMethod === 'wechat' && !formData.wechatId)
                    }
                    className="w-full py-4 bg-gradient-to-r from-[#C6A667] to-[#C6A667]/90 text-white rounded-full font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('booking.buttons.continue')}
                  </button>
                </motion.div>
              )}

              {/* Step 2: Date & Time Selection */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-[#0A1A33] mb-2 font-bold text-[20px]">
                      {isPaidSession ? t('booking.timeSelection.paidTitle') : t('booking.timeSelection.title')}
                    </h2>
                    <p className="text-[#0A1A33]/60">
                      {isPaidSession ? t('booking.timeSelection.paidSubtitle') : t('booking.timeSelection.subtitle')}
                    </p>
                    <div className="text-xs text-[#0A1A33]/50 mt-3">
                      {t('booking.timeSelection.stepOf', `Step 2 of ${totalSteps}`).replace('{current}', '2').replace('{total}', totalSteps.toString())}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column: Calendar & Time Selection */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Calendar */}
                      <div className="bg-white rounded-xl p-6 shadow-sm">
                        {/* Month Selector */}
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-[#0A1A33] font-medium">
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </h3>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={prevMonth}
                              className="p-2 rounded-lg hover:bg-[#0A1A33]/5 transition-colors"
                            >
                              <ChevronLeft className="w-5 h-5 text-[#0A1A33]" />
                            </button>
                            <button
                              type="button"
                              onClick={nextMonth}
                              className="p-2 rounded-lg hover:bg-[#0A1A33]/5 transition-colors"
                            >
                              <ChevronRight className="w-5 h-5 text-[#0A1A33]" />
                            </button>
                          </div>
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-2">
                          {/* Day Headers */}
                          {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((day) => (
                            <div key={day} className="text-center text-xs font-medium text-[#0A1A33]/50 pb-2">
                              {t(`booking.dayNames.${day}`)}
                            </div>
                          ))}
                          
                          {/* Calendar Days */}
                          {getCalendarDays().map((date, idx) => {
                            if (!date) {
                              return <div key={`empty-${idx}`} className="aspect-square" />;
                            }
                            
                            const isSelected = selectedDate?.toDateString() === date.toDateString();
                            const isToday = date.toDateString() === new Date().toDateString();
                            const isAvailable = availabilityService.isDateAvailable(date);
                            
                            return (
                              <button
                                key={idx}
                                type="button"
                                disabled={!isAvailable}
                                onClick={() => {
                                  if (isAvailable) {
                                    setSelectedDate(date);
                                    setSelectedTime(''); // Reset time when date changes
                                  }
                                }}
                                className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                                  !isAvailable
                                    ? 'text-[#0A1A33]/20 cursor-not-allowed'
                                    : isSelected
                                    ? 'bg-[#C6A667] text-white shadow-md'
                                    : isToday
                                    ? 'bg-[#0A1A33]/5 text-[#0A1A33] hover:bg-[#C6A667]/10'
                                    : 'text-[#0A1A33] hover:bg-[#0A1A33]/5'
                                }`}
                              >
                                {date.getDate()}
                              </button>
                            );
                          })}
                        </div>

                        {/* Timezone Display - Fixed to LA timezone */}
                        <div className="mt-6 pt-4 border-t border-[#0A1A33]/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs text-[#0A1A33]/60">
                              <Calendar className="w-4 h-4" />
                              <span>All times shown in Pacific Time</span>
                            </div>
                            <div className="text-xs text-[#C6A667] font-medium">
                              America/Los_Angeles
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Time Slots */}
                      {selectedDate && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-xl p-6 shadow-sm"
                        >
                          <h3 className="text-[#0A1A33] font-medium mb-1">
                            {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          </h3>
                          <p className="text-sm text-[#0A1A33]/60 mb-4">
                            {isPaidSession ? t('booking.timeSelection.paidSubtitle') : t('booking.timeSelection.timeSlots.title')}
                          </p>
                          
                          {isLoadingTimeSlots ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 border-2 border-[#C6A667]/30 border-t-[#C6A667] rounded-full animate-spin flex-shrink-0"></div>
                                <span className="text-sm text-[#0A1A33]/60">Loading available times...</span>
                              </div>
                            </div>
                          ) : availableTimeSlots.length === 0 ? (
                            <div className="text-center py-8">
                              <p className="text-sm text-[#0A1A33]/60">No available slots for this date.</p>
                              <p className="text-xs text-[#0A1A33]/40 mt-1">Please select a different date.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {availableTimeSlots
                                .filter(slot => slot.available)
                                .map((slot) => {
                                  const isSelected = isPaidSession 
                                    ? selectedTimeSlots.some(selectedSlot => 
                                        selectedSlot.date.toDateString() === selectedDate.toDateString() && 
                                        selectedSlot.time === slot.time
                                      )
                                    : selectedTime === slot.time;
                                  return (
                                    <button
                                      key={slot.time}
                                      type="button"
                                      onClick={() => {
                                        if (isPaidSession) {
                                          // For paid sessions, allow multiple selections
                                          if (isSelected) {
                                            // Remove the time slot
                                            setSelectedTimeSlots(selectedTimeSlots.filter(
                                              selectedSlot => !(selectedSlot.date.toDateString() === selectedDate.toDateString() && selectedSlot.time === slot.time)
                                            ));
                                          } else {
                                            // Add the time slot
                                            setSelectedTimeSlots([...selectedTimeSlots, { date: selectedDate, time: slot.time }]);
                                          }
                                        } else {
                                          // For free consultation, only allow one selection
                                          if (isSelected) {
                                            setSelectedTime('');
                                          } else {
                                            setSelectedTime(slot.time);
                                          }
                                        }
                                      }}
                                      className={`px-4 py-2.5 rounded-lg border-2 transition-all text-sm font-medium ${
                                        isSelected
                                          ? 'border-[#C6A667] bg-[#C6A667]/5 text-[#C6A667]'
                                          : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white text-[#0A1A33]/70 hover:bg-[#0A1A33]/5'
                                      }`}
                                    >
                                      {slot.time}
                                    </button>
                                  );
                                })}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>

                    {/* Right Panel: Appointment Summary (Desktop Only) */}
                    <div className="hidden lg:block">
                      <div className="bg-white rounded-xl p-6 shadow-sm sticky top-28">
                        <h3 className="text-[#0A1A33] font-medium mb-4">{t('booking.timeSelection.appointmentSummary.title')}</h3>
                        
                        <div className="space-y-4">
                          {/* Topic */}
                          <div>
                            <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.topic')}</div>
                            <div className="text-sm text-[#0A1A33] font-medium">
                              {getServiceLabel()}
                            </div>
                          </div>

                          {/* Name */}
                          <div>
                            <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.name')}</div>
                            <div className="text-sm text-[#0A1A33]">
                              {formData.firstName || '—'}
                            </div>
                          </div>

                          {/* Contact Info */}
                          <div>
                            <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.contact')}</div>
                            <div className="text-sm text-[#0A1A33] break-words">
                              {formData.meetingMethod === 'zoom' && (formData.email || '—')}
                              {formData.meetingMethod === 'phone' && (formData.phone ? `${formData.phoneArea} ${formData.phone}` : '—')}
                              {formData.meetingMethod === 'wechat' && (formData.wechatId || '—')}
                            </div>
                            <div className="text-xs text-[#0A1A33]/40 mt-0.5">
                              {t('booking.timeSelection.appointmentSummary.via')} {formData.meetingMethod === 'zoom' ? t('booking.form.personalInfo.meetingMethod.zoom') : formData.meetingMethod === 'phone' ? t('booking.form.personalInfo.meetingMethod.phone') : t('booking.form.personalInfo.meetingMethod.wechat')}
                            </div>
                          </div>

                          {/* Date & Time for Paid Sessions (multiple slots) */}
                          {isPaidSession && selectedTimeSlots.length > 0 && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="pt-4 border-t border-[#0A1A33]/10"
                            >
                              <div className="text-xs text-[#0A1A33]/50 mb-2">{t('booking.timeSelection.appointmentSummary.scheduledFor')}</div>
                              <div className="space-y-2">
                                {selectedTimeSlots.map((slot, index) => (
                                  <div key={index} className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="text-sm text-[#C6A667] font-medium">
                                        {slot.date.toLocaleDateString('en-US', { 
                                          weekday: 'short', 
                                          month: 'short', 
                                          day: 'numeric'
                                        })}
                                      </div>
                                      <div className="text-xs text-[#C6A667]/80">
                                        {slot.time}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedTimeSlots(selectedTimeSlots.filter((_, i) => i !== index));
                                      }}
                                      className="text-[#0A1A33]/40 hover:text-red-500 transition-colors"
                                      title={t('booking.timeSelection.appointmentSummary.remove')}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              {selectedTimeSlots.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-[#0A1A33]/10">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-[#0A1A33]/60">{t('booking.timeSelection.appointmentSummary.totalSessions')}</span>
                                    <span className="text-[#0A1A33] font-medium">{sessionCount}</span>
                                  </div>
                                  <div className="flex justify-between items-center text-sm mt-1">
                                    <span className="text-[#0A1A33]/60">{t('booking.timeSelection.appointmentSummary.totalAmount')}</span>
                                    <span className="text-[#C6A667] font-bold">${totalAmount}</span>
                                  </div>
                                </div>
                              )}
                            </motion.div>
                          )}

                          {/* Date & Time for Free Sessions (single slot) */}
                          {!isPaidSession && selectedDate && selectedTime && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="pt-4 border-t border-[#0A1A33]/10"
                            >
                              <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.scheduledFor')}</div>
                              <div className="text-sm text-[#C6A667] font-medium">
                                {selectedDate.toLocaleDateString('en-US', { 
                                  weekday: 'long', 
                                  month: 'long', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="text-sm text-[#C6A667] font-medium mt-1">
                                {selectedTime}
                              </div>
                            </motion.div>
                          )}

                          {/* Meeting Method */}
                          <div className="pt-4 border-t border-[#0A1A33]/10">
                            <div className="text-xs text-[#0A1A33]/50 mb-2">{t('booking.timeSelection.appointmentSummary.meetingMethod')}</div>
                            <div className="flex items-center gap-2">
                              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                                formData.meetingMethod === 'zoom' ? 'bg-[#C6A667]/10' : 'bg-[#0A1A33]/5'
                              }`}>
                                <Video className={`w-4 h-4 ${
                                  formData.meetingMethod === 'zoom' ? 'text-[#C6A667]' : 'text-[#0A1A33]/30'
                                }`} />
                              </div>
                              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                                formData.meetingMethod === 'phone' ? 'bg-[#C6A667]/10' : 'bg-[#0A1A33]/5'
                              }`}>
                                <Phone className={`w-4 h-4 ${
                                  formData.meetingMethod === 'phone' ? 'text-[#C6A667]' : 'text-[#0A1A33]/30'
                                }`} />
                              </div>
                              <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                                formData.meetingMethod === 'wechat' ? 'bg-[#C6A667]/10' : 'bg-[#0A1A33]/5'
                              }`}>
                                <MessageCircle className={`w-4 h-4 ${
                                  formData.meetingMethod === 'wechat' ? 'text-[#C6A667]' : 'text-[#0A1A33]/30'
                                }`} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email Confirmation Preference - Simplified */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <h3 className="text-[#0A1A33] font-medium">{t('booking.timeSelection.preferences.title')}</h3>
                      <span className="text-xs bg-[#C6A667]/10 text-[#C6A667] px-2 py-1 rounded-full font-medium">
                        {t('booking.timeSelection.preferences.required')}
                      </span>
                    </div>
                    
                    {/* Only show email confirmation preference */}
                    <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-lg hover:bg-[#0A1A33]/5 transition-all">
                      <div className="relative flex items-center justify-center w-5 h-5 mt-0.5">
                        <input
                          type="checkbox"
                          checked={addToCalendar}
                          onChange={(e) => setAddToCalendar(e.target.checked)}
                          className="sr-only peer"
                          required
                        />
                        <div className="w-5 h-5 border-2 border-[#0A1A33]/20 rounded group-hover:border-[#C6A667]/50 peer-checked:bg-[#C6A667] peer-checked:border-[#C6A667] transition-all flex items-center justify-center">
                          {addToCalendar && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-[#0A1A33] font-medium flex items-center gap-2">
                          {t('booking.timeSelection.preferences.emailConfirmation.label')}
                          <span className="text-[#C6A667]">*</span>
                        </div>
                        <div className="text-xs text-[#0A1A33]/50 mt-0.5">{t('booking.timeSelection.preferences.emailConfirmation.description')}</div>
                      </div>
                    </label>
                  </div>

                  {/* Error Message */}
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-3xl mx-auto"
                    >
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">{submitError}</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Optional Enhancements */}
                  {/* {isPaidSession && (
                    <div className="bg-white rounded-xl p-6 shadow-sm space-y-3">
                      <h3 className="text-[#0A1A33] font-medium mb-4 text-sm font-bold">{t('booking.timeSelection.preferences.title')}</h3>
                    
                      
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div className="relative flex items-center justify-center w-5 h-5 mt-0.5">
                          <input
                            type="checkbox"
                            checked={addToCalendar}
                            onChange={(e) => setAddToCalendar(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-5 h-5 border-2 border-[#0A1A33]/20 rounded group-hover:border-[#C6A667]/50 peer-checked:bg-[#C6A667] peer-checked:border-[#C6A667] transition-all flex items-center justify-center">
                            {addToCalendar && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-[#0A1A33] font-medium">{t('booking.timeSelection.preferences.addToCalendar.label')}</div>
                          <div className="text-xs text-[#0A1A33]/50 mt-0.5">{t('booking.timeSelection.preferences.addToCalendar.description')}</div>
                        </div>
                      </label>
                     
                      {formData.meetingMethod === 'phone' && (
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div className="relative flex items-center justify-center w-5 h-5 mt-0.5">
                            <input
                              type="checkbox"
                              checked={smsReminder}
                              onChange={(e) => setSmsReminder(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-5 h-5 border-2 border-[#0A1A33]/20 rounded group-hover:border-[#C6A667]/50 peer-checked:bg-[#C6A667] peer-checked:border-[#C6A667] transition-all flex items-center justify-center">
                              {smsReminder && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-[#0A1A33] font-medium">{t('booking.timeSelection.preferences.smsReminder.label')}</div>
                            <div className="text-xs text-[#0A1A33]/50 mt-0.5">{t('booking.timeSelection.preferences.smsReminder.description')}</div>
                          </div>
                        </label>
                      )}

                      {formData.meetingMethod === 'wechat' && (
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div className="relative flex items-center justify-center w-5 h-5 mt-0.5">
                            <input
                              type="checkbox"
                              checked={wechatReminder}
                              onChange={(e) => setWechatReminder(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-5 h-5 border-2 border-[#0A1A33]/20 rounded group-hover:border-[#C6A667]/50 peer-checked:bg-[#C6A667] peer-checked:border-[#C6A667] transition-all flex items-center justify-center">
                              {wechatReminder && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-[#0A1A33] font-medium">{t('booking.timeSelection.preferences.wechatReminder.label')}</div>
                            <div className="text-xs text-[#0A1A33]/50 mt-0.5">{t('booking.timeSelection.preferences.wechatReminder.description')}</div>
                          </div>
                        </label>
                      )}
                    </div>
                  )} */}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col gap-3 pt-4 max-w-3xl mx-auto">
                    <button
                      type={isPaidSession ? "button" : "submit"}
                      onClick={isPaidSession ? () => setStep(3) : undefined}
                      disabled={
                        isPaidSession 
                          ? selectedTimeSlots.length === 0
                          : (!selectedDate || !selectedTime || !addToCalendar || isSubmitting)
                      }
                      className="w-full py-4 bg-gradient-to-r from-[#C6A667] to-[#C6A667]/90 text-white rounded-full font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isPaidSession ? t('booking.buttons.continueToPayment') : (
                        isSubmitting ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin flex-shrink-0" />
                            <span>{t('booking.buttons.creatingAppointment')}</span>
                          </div>
                        ) : (
                          t('booking.buttons.confirm')
                        )
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="w-full text-center text-sm text-[#0A1A33]/60 hover:text-[#C6A667] transition-colors py-2"
                    >
                      {t('booking.buttons.back')}
                    </button>

                    <p className="text-center text-xs text-[#0A1A33]/50 italic mt-2">
                      {t('booking.confirmationNote')}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Payment (only for paid sessions) */}
              {step === 3 && isPaidSession && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-[#0A1A33] mb-2 text-[20px] font-normal font-bold">{t('booking.timeSelection.payment.title')}</h2>
                    <p className="text-[#0A1A33]/60">
                      {t('booking.timeSelection.payment.subtitle')}
                    </p>
                    <div className="text-xs text-[#0A1A33]/50 mt-3">
                      {t('booking.timeSelection.stepOf', 'Step 3 of 3').replace('{current}', '3').replace('{total}', '3')}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column: Payment Options */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Summary Box */}
                      <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-[#C6A667]/20">
                        <h3 className="text-[#0A1A33] font-medium mb-4 font-bold">{t('booking.timeSelection.payment.bookingSummary')}</h3>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between items-start pb-3 border-b border-[#0A1A33]/10">
                            <span className="text-sm text-[#0A1A33]/60">{t('booking.timeSelection.appointmentSummary.scheduledSessions')}</span>
                            <div className="text-right space-y-1">
                              {selectedTimeSlots.map((slot, index) => (
                                <div key={index}>
                                  <div className="text-sm text-[#0A1A33] font-medium">
                                    {slot.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                  <div className="text-xs text-[#0A1A33]/60">{slot.time} ({BOOKING_CONFIGS['paid-session'].duration} min)</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center pb-3 border-b border-[#0A1A33]/10">
                            <span className="text-sm text-[#0A1A33]/60">{t('booking.timeSelection.appointmentSummary.meetingMethod')}</span>
                            <span className="text-sm text-[#0A1A33] font-medium capitalize">
                              {formData.meetingMethod === 'zoom' ? 'Zoom' : formData.meetingMethod === 'phone' ? 'Phone' : 'WeChat'}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center pt-2">
                            <span className="text-[#0A1A33] font-medium">{t('booking.timeSelection.appointmentSummary.totalAmount')}</span>
                            <span className="text-2xl text-[#C6A667] font-bold">${totalAmount}</span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Method Selection */}
                      <div className="bg-white rounded-xl p-6 shadow-sm">
                        <h3 className="text-[#0A1A33] font-medium mb-4 font-bold">{t('booking.timeSelection.payment.selectPaymentMethod')}</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* PayPal */}
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentMethod('paypal')}
                            className={`p-6 rounded-xl border-2 transition-all text-left ${
                              selectedPaymentMethod === 'paypal'
                                ? 'border-[#C6A667] bg-[#C6A667]/5'
                                : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white'
                            }`}
                          >
                            <CreditCard className={`w-8 h-8 mb-3 ${
                              selectedPaymentMethod === 'paypal' ? 'text-[#C6A667]' : 'text-[#0A1A33]/40'
                            }`} />
                            <div className="font-medium text-[#0A1A33] mb-1">{t('booking.timeSelection.payment.methods.paypal.name')}</div>
                            <div className="text-xs text-[#0A1A33]/60">{t('booking.timeSelection.payment.methods.paypal.description')}</div>
                          </button>

                          {/* Venmo */}
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentMethod('venmo')}
                            className={`p-6 rounded-xl border-2 transition-all text-left ${
                              selectedPaymentMethod === 'venmo'
                                ? 'border-[#C6A667] bg-[#C6A667]/5'
                                : 'border-[#0A1A33]/10 hover:border-[#C6A667]/50 bg-white'
                            }`}
                          >
                            <DollarSign className={`w-8 h-8 mb-3 ${
                              selectedPaymentMethod === 'venmo' ? 'text-[#C6A667]' : 'text-[#0A1A33]/40'
                            }`} />
                            <div className="font-medium text-[#0A1A33] mb-1">{t('booking.timeSelection.payment.methods.venmo.name')}</div>
                            <div className="text-xs text-[#0A1A33]/60">{t('booking.timeSelection.payment.methods.venmo.description')}</div>
                          </button>
                        </div>

                        {/* Real PayPal/Venmo Payment Buttons */}
                        {(selectedPaymentMethod === 'paypal' || selectedPaymentMethod === 'venmo') && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-6"
                          >
                            {isCreatingCheckout ? (
                              <div className="flex items-center justify-center py-12">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 border-2 border-[#C6A667]/30 border-t-[#C6A667] rounded-full animate-spin flex-shrink-0"></div>
                                  <span className="text-sm text-[#0A1A33]/60">Initializing payment...</span>
                                </div>
                              </div>
                            ) : checkoutId && process.env.REACT_APP_PAYPAL_CLIENT_ID ? (
                              <>
                                {console.log('🎨 Rendering PayPalPaymentButtons', {
                                  checkoutId,
                                  paymentMethod: selectedPaymentMethod,
                                  hasClientId: !!process.env.REACT_APP_PAYPAL_CLIENT_ID,
                                  clientIdPreview: process.env.REACT_APP_PAYPAL_CLIENT_ID?.substring(0, 10) + '...'
                                })}
                                <PayPalPaymentButtons
                                  checkoutId={checkoutId}
                                  clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
                                  paymentMethod={selectedPaymentMethod as 'paypal' | 'venmo'}
                                  onSuccess={handlePaymentSuccess}
                                  onError={handlePaymentError}
                                  onCancel={() => {
                                    console.log('🚫 Payment canceled in BookingPage', {
                                      checkoutId,
                                      timestamp: new Date().toISOString()
                                    });
                                    setSelectedPaymentMethod(null);
                                  }}
                                />
                              </>
                            ) : !process.env.REACT_APP_PAYPAL_CLIENT_ID ? (
                              <>
                                {console.error('❌ CRITICAL: REACT_APP_PAYPAL_CLIENT_ID is not set in environment!')}
                                <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                                  <p className="text-sm text-yellow-800 text-center">
                                    PayPal is not configured. Please add REACT_APP_PAYPAL_CLIENT_ID to your .env.local file.
                                  </p>
                                </div>
                              </>
                            ) : (
                              <>
                                {console.warn('⚠️ Waiting for checkout to be created...', {
                                  checkoutId,
                                  isCreatingCheckout,
                                  hasClientId: !!process.env.REACT_APP_PAYPAL_CLIENT_ID
                                })}
                              </>
                            )}
                          </motion.div>
                        )}

                        {/* Payment Note */}
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs text-blue-900/70 leading-relaxed">
                            <span className="font-medium">{t('booking.timeSelection.payment.paymentNote.title')}</span> {t('booking.timeSelection.payment.paymentNote.message')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right Panel: Summary Sidebar (Desktop Only) */}
                    <div className="hidden lg:block">
                      <div className="bg-white rounded-xl p-6 shadow-sm sticky top-28">
                        <h3 className="text-[#0A1A33] font-medium mb-4">{t('booking.timeSelection.appointmentSummary.orderSummary')}</h3>
                        
                        <div className="space-y-3 mb-6">
                          <div>
                            <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.service')}</div>
                            <div className="text-sm text-[#0A1A33] font-medium">
                              {getServiceLabel()}
                            </div>
                          </div>
                          
                          <div>
                            <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.sessions')}</div>
                            <div className="text-sm text-[#0A1A33]">
                              {sessionCount} × {t('booking.timeSelection.appointmentSummary.thirtyMin')}
                            </div>
                          </div>
                          
                          <div className="pt-3 border-t border-[#0A1A33]/10">
                            <div className="text-xs text-[#0A1A33]/50 mb-1">{t('booking.timeSelection.appointmentSummary.total')}</div>
                            <div className="text-2xl text-[#C6A667] font-bold">
                              ${totalAmount}
                            </div>
                          </div>
                        </div>

                        {selectedPaymentMethod && (
                          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-xs text-green-800">{t('booking.timeSelection.payment.paymentMethodSelected')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-3xl mx-auto"
                    >
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">{submitError}</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col gap-3 pt-4 max-w-3xl mx-auto">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="w-full text-center text-sm text-[#0A1A33]/60 hover:text-[#C6A667] transition-colors py-2"
                    >
                      {t('booking.buttons.backToTimeSelection')}
                    </button>
                  </div>
                </motion.div>
              )}
            </form>
          </div>

        {/* Success Modal */}
        <AnimatePresence>
          {isSubmitted && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={resetAndGoHome}
                className="absolute inset-0 bg-[#0A1A33]/80 backdrop-blur-sm"
              />

              {/* Modal Content */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-12 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#C6A667] to-[#C6A667]/80 flex items-center justify-center"
                >
                  <Check className="w-12 h-12 text-white" />
                </motion.div>

                <h1 className="text-[#0A1A33] mb-4 font-bold text-[20px]">{t('booking.successModal.title')}</h1>
                
                {/* Conditional messages based on meeting method */}
                {formData.meetingMethod === 'zoom' && (
                  <p className="text-[#0A1A33]/70 mb-12 max-w-md mx-auto">
                    {isPaidSession 
                      ? t('booking.successModal.zoom.messageWithPayment').replace('{email}', formData.email)
                      : t('booking.successModal.zoom.message').replace('{email}', formData.email)
                    }
                  </p>
                )}

                {formData.meetingMethod === 'phone' && (
                  <p className="text-[#0A1A33]/70 mb-12 max-w-md mx-auto">
                    {isPaidSession 
                      ? t('booking.successModal.phone.messageWithPayment').replace('{phone}', `${formData.phoneArea} ${formData.phone}`)
                      : t('booking.successModal.phone.message').replace('{phone}', `${formData.phoneArea} ${formData.phone}`)
                    }
                  </p>
                )}

                {formData.meetingMethod === 'wechat' && (
                  <p className="text-[#0A1A33]/70 mb-12 max-w-md mx-auto">
                    {isPaidSession 
                      ? t('booking.successModal.wechat.messageWithPayment').replace('{wechatId}', formData.wechatId)
                      : t('booking.successModal.wechat.message').replace('{wechatId}', formData.wechatId)
                    }
                  </p>
                )}

                <div className="flex flex-col gap-3">
                  <button
                    onClick={resetAndGoHome}
                    className="w-full px-8 py-4 bg-gradient-to-r from-[#C6A667] to-[#C6A667]/90 text-white rounded-full font-medium hover:shadow-lg transition-all"
                  >
                    {t('booking.buttons.returnHome')}
                  </button>
                  <button
                    onClick={resetAndGoToServices}
                    className="w-full px-8 py-4 border-2 border-[#0A1A33]/20 text-[#0A1A33] rounded-full font-medium hover:border-[#C6A667] hover:text-[#C6A667] transition-all"
                  >
                    {t('booking.buttons.exploreServices')}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
